import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { toast } from '@/store/toastStore';

interface Term {
  id: number;
  type: string;
  title: string;
  content: string;
  effective_date: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  author_nickname: string | null;
}

const TERM_TYPES: Array<{ key: string; label: string }> = [
  { key: 'service', label: '서비스 이용약관' },
  { key: 'privacy', label: '개인정보처리방침' },
  { key: 'disclaimer', label: '투자 위험 고지' },
  { key: 'operation', label: '운영정책' },
];

function fmt(iso: string) {
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
}

function stripHtml(html: string): string {
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent ?? '').replace(/\s+/g, ' ').trim();
}

export function TermsAdminPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'terms', typeFilter],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (typeFilter) sp.set('type', typeFilter);
      return get<{ terms: Term[] }>(`/admin/terms${sp.toString() ? `?${sp}` : ''}`);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => del(`/admin/terms/${id}`),
    onSuccess: () => {
      toast.success('삭제 완료');
      qc.invalidateQueries({ queryKey: ['admin', 'terms'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '삭제 실패'),
  });

  const typeLabel = (t: string) => TERM_TYPES.find((x) => x.key === t)?.label ?? t;

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold">약관 관리</h1>
        <Button onClick={() => navigate('/admin/terms/new')}>+ 새 약관</Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5 border-b border-[var(--color-border)] pb-3">
        <button
          type="button"
          onClick={() => setTypeFilter('')}
          className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
            typeFilter === ''
              ? 'bg-[var(--color-primary)] text-[var(--color-primary-inv)] font-semibold'
              : 'text-[var(--color-text-sub)] hover:bg-[var(--color-bg)]'
          }`}
        >
          전체
        </button>
        {TERM_TYPES.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTypeFilter(t.key)}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
              typeFilter === t.key
                ? 'bg-[var(--color-primary)] text-[var(--color-primary-inv)] font-semibold'
                : 'text-[var(--color-text-sub)] hover:bg-[var(--color-bg)]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mb-4 rounded-md border border-[var(--color-border)] bg-[var(--color-info-bg)] p-3 text-xs text-[var(--color-info)]">
        같은 유형의 여러 버전 보관 가능. 사용자 페이지는 <strong>활성 상태인 가장 최신 버전</strong>만 노출돼요.
      </div>

      {isLoading ? (
        <Skeleton className="h-80" />
      ) : (
        <ul className="space-y-2">
          {data?.terms.map((t) => (
            <li
              key={t.id}
              className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
            >
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <Badge variant="outline">{typeLabel(t.type)}</Badge>
                    {t.is_active ? (
                      <Badge variant="rise">활성</Badge>
                    ) : (
                      <Badge variant="default">비활성</Badge>
                    )}
                    <Link
                      to={`/admin/terms/${t.id}/edit`}
                      className="text-base font-bold hover:underline"
                    >
                      {t.title}
                    </Link>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-[var(--color-text-sub)]">{stripHtml(t.content)}</p>
                  <div className="mt-2 text-xs text-[var(--color-text-sub)]">
                    {t.author_nickname ?? '-'} · 등록 {fmt(t.created_at)}
                    {t.effective_date && <span> · 시행 {t.effective_date}</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/admin/terms/${t.id}/edit`)}
                >
                  수정
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    if (confirm('삭제할까요?')) deleteMut.mutate(t.id);
                  }}
                >
                  삭제
                </Button>
              </div>
            </li>
          ))}
          {data?.terms.length === 0 && (
            <li className="p-8 text-center text-sm text-[var(--color-text-sub)]">
              등록된 약관이 없어요.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
